/**
 * Pure mutation functions for route actions.
 */

import {addPcbNodes, handleSetNodeRules, removeNodesAndDescendants} from "@buildwithflux/core";
import type {AnyPcbNode, IPcbLayoutNodesRuleSetsMap, PcbNode, PcbNodeTypes} from "@buildwithflux/core";
import type {IDocumentData, PcbRule} from "@buildwithflux/models";

/**
 * Pure mutation function for adding route segments.
 * Adds new PCB nodes, removes deleted nodes, and updates node positions.
 */
export function applyAddRouteSegment(
    draft: IDocumentData,
    addedNodes: AnyPcbNode[],
    deletedNodes?: AnyPcbNode[],
    updatedNodePositions?: {[uid: string]: PcbRule},
): void {
    addPcbNodes(draft.pcbLayoutNodes, addedNodes);

    if (deletedNodes) {
        removeNodesAndDescendants(
            draft.pcbLayoutNodes,
            deletedNodes.map((node) => node.uid),
        );
    }

    if (updatedNodePositions) {
        for (const [uid, position] of Object.entries(updatedNodePositions)) {
            const elementNode = draft.pcbLayoutNodes[uid];
            if (elementNode) {
                elementNode.pcbNodeRuleSet.position = position;
            }
        }
    }
}

/**
 * Pure mutation function for updating route segments.
 * Updates existing route segments in the PCB layout.
 */
export function applyUpdateRouteSegments(
    draft: IDocumentData,
    updates: {[uid: string]: PcbNode<PcbNodeTypes.routeSegment>},
): void {
    addPcbNodes(draft.pcbLayoutNodes, Object.values(updates));
}

/**
 * Pure mutation function for committing multiple routing updates.
 * Adds nodes, removes nodes, and updates node rules in a single operation.
 */
export function applyCommitMultiRoutingUpdate(
    draft: IDocumentData,
    addedNodes: AnyPcbNode[],
    deletedNodes?: string[],
    updatedNodes?: IPcbLayoutNodesRuleSetsMap,
): void {
    addPcbNodes(draft.pcbLayoutNodes, addedNodes);

    if (deletedNodes) {
        removeNodesAndDescendants(draft.pcbLayoutNodes, deletedNodes);
    }

    if (updatedNodes) {
        handleSetNodeRules(draft, updatedNodes);
    }
}
